import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import {
  CUADRO_DE_MODULO,
  FILETE_DE_MODULO,
  NOMBRE_DE_MODULO,
  type Modulo,
} from '@/components/layout/modulos';

/**
 * La tarjeta de un módulo en Inicio (artefacto «Inicio · Dashboard general»):
 * el filete de su color arriba, su cuadro, el nombre y para qué es, la flecha
 * que lleva al módulo, el número grande y tres datos chicos al pie.
 */
export function TarjetaDeModulo({
  modulo,
  icono: Icono,
  subtitulo,
  href,
  cargando,
  etiquetaDelNumero,
  numero,
  junto,
  children,
  pie,
}: {
  modulo: Modulo;
  icono: LucideIcon;
  subtitulo: string;
  href: string;
  cargando: boolean;
  etiquetaDelNumero: string;
  numero: ReactNode;
  /** Lo que va al lado del número (la variación, «vs 6 tiendas»…). */
  junto?: ReactNode;
  /** El gráfico chico del medio. */
  children?: ReactNode;
  pie: Array<{ etiqueta: string; valor: ReactNode }>;
}) {
  const nombre = NOMBRE_DE_MODULO[modulo];
  return (
    <section
      aria-labelledby={`tarjeta-${modulo}`}
      className={cn(
        'flex flex-col rounded-tarjeta border border-t-[3px] bg-card p-5 shadow-card',
        FILETE_DE_MODULO[modulo]
      )}
    >
      <header className="flex items-start gap-3">
        <span
          className={cn(
            'grid h-10 w-10 shrink-0 place-items-center rounded-xl',
            CUADRO_DE_MODULO[modulo]
          )}
        >
          <Icono className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={`tarjeta-${modulo}`} className="text-lg font-semibold leading-tight">
            {nombre}
          </h2>
          <p className="text-sm text-muted-foreground">{subtitulo}</p>
        </div>
        <Link
          to={href}
          aria-label={`Ir a ${nombre}`}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </header>

      <div className="mt-5 flex-1 space-y-3">
        <p className="text-sm text-muted-foreground">{etiquetaDelNumero}</p>
        {cargando ? (
          <Skeleton className="h-10 w-40" />
        ) : (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-titulo text-3xl font-semibold tracking-tight tabular-nums">
              {numero}
            </span>
            {junto}
          </div>
        )}
        {!cargando && children}
      </div>

      <dl className="mt-5 grid grid-cols-3 gap-3 border-t pt-4">
        {pie.map((d) => (
          <div key={d.etiqueta} className="min-w-0">
            <dt className="truncate text-xs text-muted-foreground">{d.etiqueta}</dt>
            <dd className="mt-0.5 font-titulo text-lg font-semibold tabular-nums">
              {cargando ? '…' : d.valor}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Una barra partida en tramos con su leyenda (posición en Compite, embudo en Comercia). */
export function BarraDeTramos({
  tramos,
  etiqueta,
}: {
  tramos: Array<{ clave: string; valor: number; clase: string; leyenda: string }>;
  /** Para el lector de pantalla: qué dice la barra entera. */
  etiqueta: string;
}) {
  const total = tramos.reduce((a, t) => a + t.valor, 0);
  return (
    <div className="space-y-2">
      <div
        className="flex h-2.5 w-full gap-1 overflow-hidden rounded-full"
        role="img"
        aria-label={etiqueta}
      >
        {total === 0 ? (
          <span className="h-full w-full rounded-full bg-muted" />
        ) : (
          tramos
            .filter((t) => t.valor > 0)
            .map((t) => (
              <span
                key={t.clave}
                className={cn('h-full rounded-full', t.clase)}
                style={{ width: `${(t.valor / total) * 100}%` }}
              />
            ))
        )}
      </div>
      <ul
        className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
        aria-hidden="true"
      >
        {tramos.map((t) => (
          <li key={t.clave} className="flex items-center gap-1.5">
            <span className={cn('h-2 w-2 rounded-full', t.clase)} />
            {t.leyenda}
          </li>
        ))}
      </ul>
    </div>
  );
}
